import React from 'react';
import { Link } from 'react-router-dom';
import { Column } from '../../../components/DataTable/DataTable';
import { AppStatus } from './useData';
import { FieldConfig } from '../../../components/generic/fieldRenderers';
import { FormLayout } from '../../../components/generic/FormLayoutRenderer';
import { formatDate as formatDateGlobal, APP_CONFIG } from '../../../config/appConfig';
import Badge from '../../../components/ui/Badge';
import { StatusBadgeStylePicker } from '../../../components/ui';
import {
  normalizeHexColor,
  parseStatusColorConfig,
  resolveStatusTextColor,
  serializeStatusColorConfig,
} from '../../../utils/statusColor';

const DEFAULT_APP_STATUS_COLOR = '#64748b';

const getAppStatusColor = (color?: string): string =>
  normalizeHexColor(parseStatusColorConfig(color).backgroundColor) ?? DEFAULT_APP_STATUS_COLOR;

const renderAppStatusBadge = (label?: string, color?: string) => {
  const parsedConfig = parseStatusColorConfig(color);
  const resolvedColor = normalizeHexColor(parsedConfig.backgroundColor) ?? DEFAULT_APP_STATUS_COLOR;

  return (
    <Badge
      variant="secondary"
      className="border rounded-md px-2 py-0.5"
      style={{
        backgroundColor: resolvedColor,
        borderColor: resolvedColor,
        color: resolveStatusTextColor(resolvedColor, parsedConfig.textColorMode),
      }}
    >
      {label || '-'}
    </Badge>
  );
};

export const moduleConfig = {
  module: {
    title: 'Appointment Status',
    description: 'Manage appointment status',
    i18nKey: 'app_status',
    permissionModuleKey: 'app_status',
    route: {
      list: '/app_status',
      new: '/app_status/new',
      detail: (id: number) => `/app_status/${id}`,
      edit: (id: number) => `/app_status/${id}/edit`,
    },
    api: {
      list: '/p-crm-app-status',
      detail: (id: number) => `/p-crm-app-status/${id}`,
    },
  },
  pagination: {
    initialItemsPerPage: APP_CONFIG.table.defaultPageSize,
    itemsPerPageOptions: APP_CONFIG.table.pageSizeOptions,
  },
  search: {
    placeholder: 'Search appointment status...',
  },
  filterGroups: [
    {
      name: 'basic',
      label: 'Basic',
      icon: '🔍',
      fields: ['as_descr'],
    },
  ],
  filters: {
    fields: [
      {
        key: 'as_descr',
        label: 'Description',
        placeholder: 'Type to filter description...',
        type: 'text' as const,
        getValue: (item: AppStatus) => item.as_descr,
        options: [],
      },
    ],
    applyFilter: (items: AppStatus[], filters: { as_descr: string[] | string }) => {
      return items.filter((item) => {
        const descrFilter = filters.as_descr;
        if (typeof descrFilter === 'string' && descrFilter.trim() !== '') {
          const value = (item.as_descr || '').toLowerCase();
          if (!value.includes(descrFilter.toLowerCase())) return false;
        } else if (Array.isArray(descrFilter) && descrFilter.length > 0) {
          const value = (item.as_descr || '').toLowerCase();
          const matches = descrFilter.some((filter) => {
            const filterLower = filter.toLowerCase();
            return value.includes(filterLower) || filterLower.includes(value);
          });
          if (!matches) return false;
        }
        return true;
      });
    },
  },
  toolbar: {
    actions: [
      {
        key: 'add',
        icon: 'add',
        title: 'Add New Appointment Statu',
        showTitle: false,
        variant: 'primary' as const,
        href: '/app_status/new',
      },
    ],
  },
  table: {
    actionsPosition: 'left' as const,
    onRowClick: (item: AppStatus) => {
      window.location.href = `/app_status/${item.id}`;
    },
    keyExtractor: (item: AppStatus) => item.id,
  },
  emptyStates: {
    noData: 'Get started by creating a new appointment statu.',
    noSearchResults: 'No appointment status found matching your search criteria.',
    noFilterResults: 'No appointment status match the selected filters.',
  },
  delete: {
    title: 'Delete Appointment Statu',
    message: 'Are you sure you want to delete this appointment statu? This action cannot be undone.',
    confirmText: 'Delete',
    cancelText: 'Cancel',
    errorMessage: 'Failed to delete appointment statu. Please try again.',
    handler: async (id: number) => {
      const { deleteAppStatus } = await import('../../../services/mutations');
      await deleteAppStatus(id);
    },
  },
  utils: {
    formatDate: (dateString?: string) => {
      if (!dateString) return '-';
      return formatDateGlobal(dateString);
    },
  },
  detail: {
    subtitle: 'Appointment Statu Details',
    notFoundTitle: 'Appointment Statu not found',
    notFoundMessage: 'The appointment statu you\'re looking for doesn\'t exist.',
    notFoundBackText: 'Back to Appointment Status',
    defaultTitle: 'Unnamed Appointment Statu',
    getTitle: (item: AppStatus) => {
      return item.as_descr || `Appointment Statu #${item.id}`;
    },
  },
  form: {
    new: {
      title: 'New Appointment Statu',
      subtitle: 'Add a new appointment statu',
      submitText: 'Create Appointment Statu',
    },
    edit: {
      title: 'Edit Appointment Statu',
      subtitle: 'Update appointment statu information',
      submitText: 'Update Appointment Statu',
    },
    cancelText: 'Cancel',
    loadingText: 'Loading appointment statu...',
    errorMessage: 'Failed to save appointment statu. Please try again.',
    create: async (data: Partial<AppStatus>) => {
      const { createAppStatus } = await import('../../../services/mutations');
      return await createAppStatus(data);
    },
    update: async (id: number, data: Partial<AppStatus>) => {
      const { updateAppStatus } = await import('../../../services/mutations');
      return await updateAppStatus(id, data);
    },
  },
  fields: [
    {
      name: 'as_descr',
      label: 'Description',
      type: 'text',
      required: true,
      placeholder: 'Enter appointment statu description',
      validation: {
        required: true,
        minLength: 1,
        maxLength: 255,
      },
    },
    {
      name: 'as_color',
      label: 'Badge Style',
      type: 'custom',
      required: true,
      helperText: 'Pick the background color and how the badge text should be rendered.',
      validation: {
        required: true,
      },
      customRender: ({ value, error, formData, setMultipleFieldValues }) => {
        const parsedConfig = parseStatusColorConfig(
          typeof value === 'string'
            ? value
            : serializeStatusColorConfig(DEFAULT_APP_STATUS_COLOR, 'auto'),
        );

        return (
          <StatusBadgeStylePicker
            backgroundColor={parsedConfig.backgroundColor}
            textColorMode={parsedConfig.textColorMode}
            error={error}
            helperText="This will be used in appointment and lead status badges and status selection."
            previewLabel={formData?.as_descr || 'Preview'}
            onChange={(backgroundColor, textColorMode) => {
              setMultipleFieldValues?.({
                as_color: serializeStatusColorConfig(backgroundColor, textColorMode),
              });
            }}
          />
        );
      },
    },
  ] as FieldConfig[],
  formLayout: {
    mode: 'default',
    sections: [
      {
        id: 'basic',
        title: 'Appointment Statu Information',
        icon: '📝',
        fields: ['as_descr', 'as_color'],
        columns: 2,
        collapsible: false,
      },
    ],
  } as FormLayout,
};

export const getColumns = (formatDate: (dateString?: string) => string): Column<AppStatus>[] => [
  {
    key: 'id',
    header: 'ID',
    sortable: true,
    render: (value) => (
      <span className="text-sm text-gray-900">{value || '-'}</span>
    ),
  },
  {
    key: 'as_descr',
    header: 'Description',
    sortable: true,
    render: (value, row) => renderAppStatusBadge(value, row.as_color),
  },
  {
    key: 'as_color',
    header: 'Color',
    sortable: true,
    render: (_value, row) => {
      const resolvedColor = getAppStatusColor(row.as_color);
      return (
        <div className="flex items-center gap-2">
          <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: resolvedColor }} />
          <span className="text-sm text-gray-700">{resolvedColor}</span>
        </div>
      );
    },
  },
];

export const getDetailSections = (
  item: AppStatus,
  formatDate: (dateString?: string) => string
) => {
  return [
    {
      title: 'Basic Information',
      icon: '📝',
      fields: [
        {
          label: 'Description',
          value: renderAppStatusBadge(item.as_descr, item.as_color),
        },
        {
          label: 'Color',
          value: (
            <div className="flex items-center gap-2">
              <span className="h-4 w-4 rounded-full border border-black/10" style={{ backgroundColor: getAppStatusColor(item.as_color) }} />
              <span>{getAppStatusColor(item.as_color)}</span>
            </div>
          ),
        },
      ],
    },
  ];
};

export const getStatusBadges = (item: AppStatus) => {
  return renderAppStatusBadge(item.as_descr, item.as_color);
};

export const getInitialFormData = (): Partial<AppStatus> => {
  return {
    as_descr: '',
    as_color: serializeStatusColorConfig(DEFAULT_APP_STATUS_COLOR, 'auto'),
  };
};

export const mapItemToFormData = (item: AppStatus): Partial<AppStatus> => {
  return {
    as_descr: item.as_descr || '',
    as_color: item.as_color || serializeStatusColorConfig(DEFAULT_APP_STATUS_COLOR, 'auto'),
  };
};

export const getToolbarIcon = (iconName: string): React.ReactNode => {
  switch (iconName) {
    case 'add':
      return (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      );
    default:
      return null;
  }
};

export const getTableActions = (
  item: AppStatus,
  onDelete: (id: number) => void,
  _a?: unknown,
  _b?: unknown,
  options?: { canDelete?: boolean; canEdit?: boolean }
): React.ReactNode => {
  const canDelete = options?.canDelete !== false;
  const canEdit = options?.canEdit !== false;
  return (
    <div className="flex items-center space-x-2 flex-nowrap">
      <Link 
        to={moduleConfig.module.route.detail(item.id)}
        className="p-2 text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50 rounded-lg transition-colors"
        onClick={(e) => e.stopPropagation()}
        title="View"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
      </Link>
      {canEdit && (
        <Link 
          to={moduleConfig.module.route.edit(item.id)}
          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 rounded-lg transition-colors"
          onClick={(e) => e.stopPropagation()}
          title="Edit"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </Link>
      )}
      {canDelete && (
        <button 
          onClick={(e) => {
            e.stopPropagation();
            onDelete(item.id);
          }}
          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 rounded-lg transition-colors"
          title="Delete"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
      )}
    </div>
  );
};
